import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Star, ChevronDown } from "lucide-react";

/**
 * LoyaltyPanel — pulls the customer's balance for the entered phone and lets
 * them redeem exactly one reward per order. Reward tiers are fetched
 * dynamically from `/api/loyalty/{phone}` (which reads them from the DB),
 * so admin-created rewards appear here automatically.
 *
 * Emits selection via onChange({ key, choice, price }) so the checkout can
 * subtract the free item's price from the total.
 */
export default function LoyaltyPanel({ phone, value, onChange }) {
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => setMenu([]));
  }, []);

  useEffect(() => {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length < 7) {
      setData(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/loyalty/${digits}`);
        setData(data);
      } catch {
        setData(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [phone]);

  /** name → price map for O(1) lookup when computing the discount. */
  const priceMap = useMemo(() => {
    const m = {};
    for (const it of menu) if (it.available) m[it.name] = it.price;
    return m;
  }, [menu]);

  if (!data) return null;

  const availableSet = new Set(
    data.rewards.filter((r) => r.available).map((r) => r.key)
  );
  const isRedeeming = !!value?.key;

  /** Emit selection and include the item's price so the caller can discount. */
  const emit = (key, choice) => {
    if (!key) return onChange(null);
    const price = choice ? Number(priceMap[choice] || 0) : 0;
    onChange({ key, choice, price });
  };

  return (
    <section
      data-testid="loyalty-panel"
      className="bg-white rounded-xl border border-stone-200 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
        data-testid="loyalty-toggle"
      >
        <div className="w-9 h-9 rounded-full bg-brand-yellow/30 flex items-center justify-center shrink-0">
          <Star className="w-4 h-4 text-yellow-700" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
            Loyalty Rewards
          </div>
          <div className="text-sm text-stone-800 font-bold" data-testid="loyalty-balance">
            {data.points} points{" "}
            {isRedeeming && (
              <span className="text-emerald-700 text-xs font-black">· REWARD APPLIED</span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-stone-400 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="border-t border-stone-100 p-4 space-y-3">
          <div className="text-[11px] text-stone-500 leading-relaxed">
            Earn <span className="font-bold text-stone-800">1 point per $1</span> spent.
            Pick <span className="font-bold text-stone-800">one</span> free item per order.
          </div>

          {data.rewards.map((tier) => {
            const unlocked = availableSet.has(tier.key);
            const active = value?.key === tier.key;
            const items = tier.fixed_item
              ? menu.filter((m) => m.name === tier.fixed_item && m.available)
              : menu.filter((m) => m.category === tier.menu_category && m.available);
            const toGo = Math.max(0, tier.points - data.points);
            const sub = tier.fixed_item
              ? `${tier.points} pts · ${tier.fixed_item.toLowerCase()}`
              : `${tier.points} pts · pick any ${(tier.menu_category || "").toLowerCase().replace(/s$/, "")}`;

            return (
              <div
                key={tier.key}
                data-testid={`loyalty-tier-${tier.key}`}
                className={`border rounded-lg p-3 space-y-2 transition-colors ${
                  active
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-stone-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5 text-brand-red shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-stone-800">{tier.label}</div>
                    <div className="text-[11px] text-stone-500">{sub}</div>
                  </div>
                  {!unlocked && (
                    <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">
                      {toGo > 0 ? `${toGo} to go` : "Locked"}
                    </span>
                  )}
                  {unlocked && active && (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white font-black px-2.5 py-1 rounded-full">
                      Applied ✓
                    </span>
                  )}
                </div>

                {unlocked && (
                  tier.fixed_item ? (
                    // Single fixed item — one-tap redeem
                    <button
                      type="button"
                      onClick={() => {
                        if (active) return emit(null);
                        const it = items[0];
                        if (!it) return;
                        emit(tier.key, it.name);
                      }}
                      disabled={items.length === 0}
                      data-testid={`loyalty-redeem-${tier.key}`}
                      className={`w-full text-xs font-black uppercase tracking-widest px-3 py-2 rounded-full transition-colors ${
                        active
                          ? "bg-emerald-600 text-white"
                          : "bg-brand-red text-white hover:bg-red-800"
                      } disabled:opacity-50`}
                    >
                      {items.length === 0 ? "Unavailable" : active ? "Remove" : "Redeem"}
                    </button>
                  ) : (
                    <select
                      value={active ? value.choice || "" : ""}
                      onChange={(e) =>
                        e.target.value ? emit(tier.key, e.target.value) : emit(null)
                      }
                      data-testid={`loyalty-redeem-${tier.key}-select`}
                      className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-bold"
                    >
                      <option value="">— Pick a {(tier.menu_category || "").replace(/s$/, "").toLowerCase()} to redeem —</option>
                      {items.map((it) => (
                        <option key={it.name} value={it.name}>
                          {it.name} (${it.price.toFixed(2)})
                          {active && value?.choice === it.name ? " ✓" : ""}
                        </option>
                      ))}
                    </select>
                  )
                )}
              </div>
            );
          })}

          {availableSet.size === 0 && (
            <div className="text-[11px] text-stone-400 text-center pt-1">
              Keep ordering to unlock rewards!
            </div>
          )}
        </div>
      )}
    </section>
  );
}
